/**
 * Acciones de cobro compartidas por PlanSelectionPage, SubscriptionPage
 * (banner de trial) y SubscriptionExpiredPage (paywall).
 */

import { updateSubscription, activateMercadoPagoSubscription } from '../services/api';
import { isValidEmail } from './payerEmail';

export const INVALID_PAYER_EMAIL = 'INVALID_PAYER_EMAIL';

/**
 * ¿El error es "no hay email de pagador válido"? Lo manda el backend como
 * 400 `{ code: 'INVALID_PAYER_EMAIL' }` cuando tampoco encuentra un fallback.
 */
export const isInvalidPayerEmailError = (err) =>
  err?.code === INVALID_PAYER_EMAIL || err?.response?.data?.code === INVALID_PAYER_EMAIL;

/**
 * Inicia el checkout de Mercado Pago y devuelve el `init_point` al que hay que
 * redirigir (URL externa de MP).
 *
 * - Cambio de plan con débito de MP ya activo: el backend cancela la
 *   preapproval anterior y responde `mpReauthorization.init_point` (nueva
 *   suscripción pendiente). Se redirige a ese y NO se llama a activate-mp (que
 *   crearía una segunda preapproval).
 * - El email solo se manda si tiene formato válido (usar getPayerEmail; nunca
 *   user.username = sub de Cognito). Si no, va null y el backend resuelve su
 *   propio fallback o responde 400 INVALID_PAYER_EMAIL.
 *
 * @param {object} params
 * @param {string} params.subscriptionId
 * @param {string} params.planId
 * @param {string} params.billingInterval 'monthly' | 'annual'
 * @param {string} params.currency
 * @param {string} params.payerEmail
 * @param {boolean} [params.activateOnly] true = no tocar la suscripción (mismo
 *   plan en trial): solo activate MP, que difiere el primer cobro al fin del
 *   trial. Con update + paymentMethodId el backend pasa el trial a active.
 * @returns {Promise<string|null>} init_point
 */
export async function startMercadoPagoCheckout({
  subscriptionId,
  planId,
  billingInterval,
  currency,
  payerEmail,
  activateOnly = false,
}) {
  const email = isValidEmail(payerEmail) ? payerEmail.trim() : null;
  if (!activateOnly) {
    const updated = await updateSubscription(subscriptionId, {
      planId,
      billingInterval,
      currency,
      paymentMethodId: 'pending',
    });
    const reauthInitPoint = updated?.mpReauthorization?.init_point;
    if (reauthInitPoint) return reauthInitPoint;
  }
  const { init_point: initPoint } = await activateMercadoPagoSubscription(subscriptionId, email);
  return initPoint || null;
}

/**
 * FREE legacy → Starter/Profesional con 30 días sin cargo (sin Mercado Pago).
 * El backend devuelve la suscripción en `status: 'trial'` con `trialEnd`.
 */
export async function startTrialUpgrade({ subscriptionId, planId, billingInterval, currency }) {
  return updateSubscription(subscriptionId, {
    planId,
    billingInterval,
    currency,
    trialEnabled: true,
  });
}
